import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCircuitLeaderboard } from '@/hooks/useCircuitLeaderboard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Trophy, Loader2, ChevronDown, Calendar } from 'lucide-react';

interface OrganizerRankingTabProps {
  organizerId: string;
}

const OrganizerRankingTab = ({ organizerId }: OrganizerRankingTabProps) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [hasParticipated, setHasParticipated] = useState<boolean | null>(null);
  const [circuitId, setCircuitId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [circuitName, setCircuitName] = useState<string>('');
  const [sports, setSports] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');

  const { leaderboard, tournaments, loading: leaderboardLoading } = useCircuitLeaderboard(
    circuitId,
    selectedCategory,
    selectedSubcategory
  );

  // Check participation & get circuit_id
  useEffect(() => {
    const init = async () => {
      // Get circuit_id from organizer profile
      const { data: orgProfile } = await supabase
        .from('organizer_public_profiles')
        .select('ranking_display_circuit_id')
        .eq('user_id', organizerId)
        .maybeSingle();

      const cId = orgProfile?.ranking_display_circuit_id || null;
      setCircuitId(cId);

      if (!user) {
        setHasParticipated(false);
        setLoading(false);
        return;
      }

      // Check if user participated in organizer's tournaments
      const { data: events } = await supabase
        .from('events')
        .select('id')
        .eq('organizer_id', organizerId)
        .eq('event_type', 'tournament');

      if (!events?.length) {
        setHasParticipated(false);
        setLoading(false);
        return;
      }

      const { count } = await supabase
        .from('registrations')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('status', 'confirmed')
        .in('event_id', events.map(e => e.id));

      setHasParticipated((count || 0) > 0);
      setLoading(false);
    };
    init();
  }, [user, organizerId]);

  // Fetch filter options from circuit leaderboard
  useEffect(() => {
    if (!circuitId) return;
    const fetchSports = async () => {
      const { data } = await supabase
        .from('ranking_circuits')
        .select('sport, name')
        .eq('id', circuitId)
        .maybeSingle();
      if (data?.sport) {
        setSports([data.sport]);
        setSelectedSport(data.sport);
      }
      if (data?.name) {
        setCircuitName(data.name);
      }
    };
    fetchSports();
  }, [circuitId]);

  useEffect(() => {
    if (!circuitId) return;
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('ranking_circuit_leaderboard')
        .select('category')
        .eq('circuit_id', circuitId);
      const unique = [...new Set((data || []).map((r: any) => r.category))].sort();
      setCategories(unique);
      setSelectedCategory(unique.length > 0 ? unique[0] : '');
      setSelectedSubcategory('');
    };
    fetchCategories();
  }, [circuitId]);

  useEffect(() => {
    if (!circuitId || !selectedCategory) return;
    const fetchSubcategories = async () => {
      const { data } = await supabase
        .from('ranking_circuit_leaderboard')
        .select('subcategory')
        .eq('circuit_id', circuitId)
        .eq('category', selectedCategory);
      const unique = [...new Set((data || []).map((r: any) => r.subcategory))].sort();
      setSubcategories(unique);
      setSelectedSubcategory(unique.length > 0 ? unique[0] : '');
    };
    fetchSubcategories();
  }, [selectedCategory, circuitId]);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  if (loading || hasParticipated === null) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (hasParticipated === false) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          {t('organizerProfile.registerToSeeRanking')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Circuit Name */}
      {circuitName && (
        <p className="text-sm font-semibold text-foreground">{circuitName}</p>
      )}
      {/* Filters */}
      <div className={`grid gap-2 ${sports.length > 1 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {sports.length > 1 && (
          <Select value={selectedSport} onValueChange={setSelectedSport}>
            <SelectTrigger className="text-xs h-9">
              <SelectValue placeholder={t('organizerProfile.sport')} />
            </SelectTrigger>
            <SelectContent>
              {sports.map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="text-xs h-9">
            <SelectValue placeholder={t('organizerProfile.category')} />
          </SelectTrigger>
          <SelectContent>
            {categories.map(c => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
          <SelectTrigger className="text-xs h-9">
            <SelectValue placeholder={t('organizerProfile.subcategory')} />
          </SelectTrigger>
          <SelectContent>
            {subcategories.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {leaderboardLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Collapsible tournaments list */}
          {tournaments.length > 0 && (
            <Collapsible>
              <CollapsibleTrigger className="flex items-center justify-between w-full px-1 py-1 group">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  Torneos del circuito ({tournaments.length})
                </p>
                <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="space-y-1.5 mt-2">
                  {tournaments.map((t, idx) => (
                    <div key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/30 border">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold shrink-0">
                        T{idx + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{t.name}</p>
                        <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(t.date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Ranking table with per-tournament columns */}
          {leaderboard.length === 0 ? (
            <div className="text-center py-8">
              <Trophy className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t('organizerProfile.noRankingData')}</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-1">
                Ranking — {selectedCategory}{selectedSubcategory ? ` · ${selectedSubcategory}` : ''}
              </p>
              <div className="rounded-lg border overflow-hidden bg-card">
                <div className="overflow-x-auto">
                  <div className="min-w-fit">
                    {/* Header */}
                    <div className="flex text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2 bg-muted/30 border-b">
                      <span className="w-8 shrink-0">#</span>
                      <span className="flex-1 min-w-[100px]">{t('organizerProfile.player')}</span>
                      {tournaments.map((t, idx) => (
                        <span key={t.id} className="w-12 text-center shrink-0">T{idx + 1}</span>
                      ))}
                      <span className="w-14 text-right shrink-0">Total</span>
                    </div>
                    {/* Rows */}
                    <div className="divide-y divide-border">
                      {leaderboard.map((entry, idx) => (
                        <div
                          key={idx}
                          className={`flex items-center px-3 py-2 text-sm ${
                            entry.isCurrentUser ? 'bg-primary/10 font-semibold' : 'bg-card'
                          }`}
                        >
                          <span className="w-8 shrink-0 text-muted-foreground font-medium">{entry.position}</span>
                          <span className="flex-1 min-w-[100px] truncate text-foreground">
                            {entry.name}
                            {entry.isCurrentUser && <span className="text-primary ml-1 text-xs">(tú)</span>}
                          </span>
                          {tournaments.map(t => (
                            <span key={t.id} className="w-12 text-center shrink-0 text-xs text-muted-foreground">
                              {entry.pointsPerTournament[t.id] ?? '-'}
                            </span>
                          ))}
                          <span className="w-14 text-right shrink-0 font-medium text-foreground">{entry.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OrganizerRankingTab;
